import React, { FC, useState } from 'react';

import { useTranslation } from '@modernloop/i18n';
import { Dialog } from '@modernloop/shared/components';
import { FormControlLabel, Radio, RadioGroup } from '@mui/material';
import { useSnackbar } from 'notistack';

import { StringOrgPref } from 'src/generated/mloop-graphql';

import { useOrgPrefStringTyped, useSetOrgPrefStringTyped } from 'src/hooks/api/org';
import { useApolloRefetchQuery } from 'src/hooks/useApolloQuery';

import { SettingsRow } from 'src/views-new/Settings/shared/layout';

type Props = {
  /** When provided, renders the candidate-name row backed by this per-tool pref. */
  namePref?: StringOrgPref;
  /** When provided, renders the candidate-email row backed by this per-tool pref. */
  emailPref?: StringOrgPref;
  disabled: boolean;
};

type IdentityOption = { value: string; label: string };

type IdentityDialogProps = {
  title: string;
  options: IdentityOption[];
  initialValue: string;
  onClose: () => void;
  onSubmit: (value: string) => void;
};

/** Radio-option dialog matching the Privacy settings dialogs (Dialog + RadioGroup + Save/Cancel). */
const IdentitySelectDialog: FC<IdentityDialogProps> = ({ title, options, initialValue, onClose, onSubmit }) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(initialValue);

  return (
    <Dialog
      open
      title={title}
      onClose={onClose}
      submitOptions={{ label: t('hiring-dashboard-common:common.save'), onClick: () => onSubmit(selected) }}
      cancelOptions={{ label: t('hiring-dashboard-common:common.cancel'), onClick: onClose }}
    >
      <RadioGroup value={selected} onChange={(event) => setSelected(event.target.value)}>
        {options.map((option) => (
          <FormControlLabel
            key={option.value}
            control={<Radio />}
            label={option.label}
            checked={option.value === selected}
            value={option.value}
          />
        ))}
      </RadioGroup>
    </Dialog>
  );
};

type IdentityRowProps = {
  pref: StringOrgPref;
  label: string;
  subtitle: string;
  options: IdentityOption[];
  /** Enum value used when the pref is unset (send the candidate's real value). */
  defaultValue: string;
  disabled: boolean;
};

/**
 * A single candidate-identity row (name or email) backed by one per-tool org pref. Owns its own
 * pref hooks so it can be rendered conditionally without violating the rules of hooks. When set to
 * anonymize, the vendor never receives the candidate's real value (email omitted; name replaced
 * with a non-identifying "Application <id>" label). Follows the Privacy settings pattern:
 * SettingsRow -> radio dialog -> save with OrgPrefs refetch and toast.
 */
const IdentitySettingRow: FC<IdentityRowProps> = ({ pref, label, subtitle, options, defaultValue, disabled }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const refetch = useApolloRefetchQuery();
  const [savedValue] = useOrgPrefStringTyped(pref);
  const [open, setOpen] = useState(false);

  const handleSaved = async () => {
    setOpen(false);
    await refetch(['OrgPrefs']);
    enqueueSnackbar(t('hiring-dashboard-common:common.toast.changesSaved'), { variant: 'success' });
  };
  const setPref = useSetOrgPrefStringTyped(pref, handleSaved);

  const selectedValue = savedValue ?? defaultValue;
  const selectedOption = options.find((option) => option.value === selectedValue) ?? options[0];

  return (
    <>
      <SettingsRow
        settingName={label}
        settingSubtitle={subtitle}
        settingValue={selectedOption.label}
        onClick={() => setOpen(true)}
        disabled={disabled}
      />
      {open && (
        <IdentitySelectDialog
          title={label}
          options={options}
          initialValue={selectedValue}
          onClose={() => setOpen(false)}
          onSubmit={(value) => setPref(value)}
        />
      )}
    </>
  );
};

/**
 * Candidate identity (name/email) sent to a coding tool, backed by per-tool org prefs. Renders the
 * name row only when `namePref` is provided and the email row only when `emailPref` is provided, so
 * a tool never shows a control for a field it cannot send (e.g. CodeSignal has no title but does
 * send name + email; Codility sends email but not name). When set to omit, the email is dropped
 * entirely and the name is replaced with a non-identifying "Application <id>" label derived from the
 * application's ATS id, so the vendor never receives candidate PII.
 */
const CandidateIdentitySettings: FC<Props> = ({ namePref, emailPref, disabled }) => {
  const { t } = useTranslation();

  const nameOptions: IdentityOption[] = [
    {
      value: 'CANDIDATE_NAME',
      label: t('hiring-dashboard-settings-integrations:settings.collaborationLinks.identity.nameOption.candidateName'),
    },
    {
      value: 'ANONYMIZED_NAME',
      label: t('hiring-dashboard-settings-integrations:settings.collaborationLinks.identity.nameOption.anonymizedName'),
    },
    {
      value: 'OMIT_NAME',
      label: t('hiring-dashboard-settings-integrations:settings.collaborationLinks.identity.nameOption.omitName'),
    },
  ];
  const emailOptions: IdentityOption[] = [
    {
      value: 'CANDIDATE_EMAIL',
      label: t(
        'hiring-dashboard-settings-integrations:settings.collaborationLinks.identity.emailOption.candidateEmail'
      ),
    },
    {
      value: 'OMIT_EMAIL',
      label: t(
        'hiring-dashboard-settings-integrations:settings.collaborationLinks.identity.emailOption.anonymizedEmail'
      ),
    },
  ];

  return (
    <>
      {namePref && (
        <IdentitySettingRow
          pref={namePref}
          label={t('hiring-dashboard-settings-integrations:settings.collaborationLinks.identity.nameLabel')}
          subtitle={t('hiring-dashboard-settings-integrations:settings.collaborationLinks.identity.nameSubtitle')}
          options={nameOptions}
          defaultValue="CANDIDATE_NAME"
          disabled={disabled}
        />
      )}
      {emailPref && (
        <IdentitySettingRow
          pref={emailPref}
          label={t('hiring-dashboard-settings-integrations:settings.collaborationLinks.identity.emailLabel')}
          subtitle={t('hiring-dashboard-settings-integrations:settings.collaborationLinks.identity.emailSubtitle')}
          options={emailOptions}
          defaultValue="CANDIDATE_EMAIL"
          disabled={disabled}
        />
      )}
    </>
  );
};

export default CandidateIdentitySettings;
